import { createContext, useContext, useEffect } from 'react'

type StepRegistry = {
  register: (path: string) => void
  unregister: (path: string) => void
}

export const StepRegistryContext = createContext<StepRegistry | null>(null)

// Registers a set of field paths with the enclosing Step without binding inputs.
// For relocating existing field components (e.g. the attendees box) into a step:
// they keep binding via form.getInputProps, while this lets the step validate them.
// No-op outside a Step. Pass a referentially-stable `paths` (memoize it).
export const useRegisterStepFields = (paths: string[]) => {
  const registry = useContext(StepRegistryContext)

  useEffect(() => {
    if (!registry) {
      return
    }
    paths.forEach(path => registry.register(path))
    return () => paths.forEach(path => registry.unregister(path))
  }, [registry, paths])
}

export const INVALID_FIELD_SELECTOR =
  '[data-invalid="true"], [aria-invalid="true"]'

// Slightly longer than Mantine Collapse's default 200ms transition, so a step that
// gets expanded here is fully open (and the field has its final layout) before we scroll.
const COLLAPSE_OPEN_MS = 250

// On a failed submit: expand every step whose subtree contains an invalid field,
// then scroll the first invalid field into view. Form-agnostic for easy testing —
// the caller supplies how to expand a step by id.
export const openInvalidStepsAndScroll = (
  container: HTMLElement | null,
  setStepExpanded: (id: string, expanded: boolean) => void,
) => {
  if (!container) {
    return
  }

  container.querySelectorAll<HTMLElement>('[data-cr-step]').forEach(stepEl => {
    const id = stepEl.getAttribute('data-cr-step')
    if (id && stepEl.querySelector(INVALID_FIELD_SELECTOR)) {
      setStepExpanded(id, true)
    }
  })

  // Wait for the open animation to finish so the field is in its final position,
  // otherwise we scroll to the still-collapsed (height 0) location.
  setTimeout(() => {
    container
      .querySelector<HTMLElement>(INVALID_FIELD_SELECTOR)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, COLLAPSE_OPEN_MS)
}
